import React, { useState, useCallback, useEffect } from "react";
import { Button, Container } from "react-bootstrap";
import * as Icons from "react-bootstrap-icons";
import FlowReport, { FlowReportGrid } from "../FlowReport/FlowReport";
import { FlowReport as FlowReportType } from "../../reusable-components/WorkFlowModals/FlowReportModal/FlowReportModal.utils";
import styles from "./FlowReport8879.module.css";
import { makeCygnusApiCall } from "../../api/api";

/**
 * Multi-Flow Configuration
 * Defines the structure of flows, fields, and columns for multi-flow reports
 */
interface MultiFlowConfig {
  reportType: string;
  reportName: string;
  reportSubtitle?: string;
  joinField: string;
  fallbackJoinField?: string;
  flows: Array<{
    flowId: number;
    alias: string;
    displayName: string;
    flowName?: string;
    fields: Array<{
      source: string;
      header: string;
      type: string;
      visible: boolean;
      sortable?: boolean;
      filterable?: boolean;
      filterValues?: string[];
    }>;
    includeReportType?: boolean;
    reportTypeHeader?: string;
    includeActions?: boolean;
  }>;
  baseColumns: Array<{
    source: string;
    header: string;
    type: string;
    visible: boolean;
    sortable?: boolean;
    filterable?: boolean;
    width?: number;
    flex?: number;
  }>;
}

/**
 * Flow Report Configuration from Backend
 *
 * Supports two modes:
 * - Single: One flow with direct FlowId
 * - Multi: Multiple flows joined by CygnusExecutionId
 */
interface FlowReportConfig {
  isConfigured: boolean;
  reportMode: "single" | "multi" | null;
  reportType: string;
  beautifiedNames: Record<string, string>;

  // Single mode fields
  flowId?: number;
  flowName?: string;
  baseColumnHeaders?: Record<string, string>;

  // Multi mode fields
  config?: MultiFlowConfig;
}

/**
 * FlowReport8879 Page Component
 *
 * Dedicated page for displaying the 8879 Form Tracking report.
 *
 * Features:
 * - Fetches configuration from backend to determine report mode
 * - Supports single-flow mode (original) and multi-flow mode (new)
 * - Dynamic action columns parsed from ReportLog
 * - Beautified column headers from backend configuration
 * - Shows configuration error if flow not set up for firm
 *
 * Report Modes:
 * - Single: Uses /workflow/flow/report/ and /workflow/flow/grid/
 * - Multi: Uses /workflow/flow/multi-report/ and /workflow/flow/multi-grid/
 */
const FlowReport8879: React.FC = () => {
  // Configuration State
  const [config, setConfig] = useState<FlowReportConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  // Auth State
  const [token, setToken] = useState<string | null>(null);

  // Report State
  const [refreshKey, setRefreshKey] = useState(0);
  
  /**
   * Initialize authentication data from localStorage
   */
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    setToken(storedToken);

    if (!storedToken) {
      console.warn("[FlowReport8879] Token not found in localStorage");
    }
  }, []);

  /**
   * Fetch Configuration from Backend
   *
   * Determines report mode (single vs multi) and loads
   * flow configuration, beautified names, and column headers.
   */
  useEffect(() => {
    if (!token) return;

    const fetchConfig = async () => {
      setConfigLoading(true);
      setConfigError(null);

      try {
        const response = await makeCygnusApiCall({
          url: `/flow/config/?reportType=8879`,
          method: "GET",
          token,
        });

        if (response.success && response.data) {
          const data = response.data as FlowReportConfig;

          if (!data.isConfigured) {
            setConfigError(
              response.message ||
                "The Flow for 8879 report has not been configured for this firm."
            );
            setConfig(null);
          } else {
            setConfig(data);
            console.log("[FlowReport8879] Config loaded:", {
              mode: data.reportMode,
              flowId: data.flowId,
              multiFlowCount: data.config?.flows?.length,
            });
          }
        } else {
          setConfigError(
            response.message ||
              "The Flow for 8879 report has not been configured for this firm."
          );
        }
      } catch (err) {
        console.error("[FlowReport8879] Failed to fetch config:", err);
        setConfigError(
          "Failed to load report configuration. Please try again later."
        );
      } finally {
        setConfigLoading(false);
      }
    };

    fetchConfig();
  }, [token]);

  const handleReportLoaded = useCallback(
    (report: FlowReportType, grid: FlowReportGrid | null) => {
      console.log("[FlowReport8879] Report loaded:", report.meta.title);
    },
    []
  );

  const handleReportError = useCallback((error: Error) => {
    console.error("[FlowReport8879] Report error:", error);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // =========================================================================
  // RENDER: Loading State
  // =========================================================================
  if (configLoading) {
    return (
      <Container className={styles.pageContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p>Loading report configuration...</p>
        </div>
      </Container>
    );
  }

  if (!token) {
    return (
      <Container className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <Icons.ExclamationTriangle size={48} className={styles.errorIcon} />
          <h3>Authentication Required</h3>
          <p>Unable to load report. Authentication token not found.</p>
          <Button
            variant="primary"
            onClick={() => (window.location.href = "/login")}
          >
            Go to Login
          </Button>
        </div>
      </Container>
    );
  }


  if (configError || !config) {
    return (
      <Container className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <Icons.GearWideConnected
            size={48}
            className={styles.configErrorIcon}
          />
          <h3>Report Not Configured</h3>
          <p>{configError || "Flow not configured for this firm."}</p>
          <div className={styles.errorActions}>
            <Button
              variant="outline-secondary"
              onClick={() => window.history.back()}
            >
              <Icons.ArrowLeft size={16} /> Go Back
            </Button>
            <Button variant="primary" onClick={() => window.location.reload()}>
              <Icons.ArrowClockwise size={16} /> Try Again
            </Button>
          </div>
        </div>
      </Container>
    );
  }


  const isMultiMode = config.reportMode === "multi";

  // Shared loading component
  const loadingComponent = (
    <div className={styles.loadingContainer}>
      <div className={styles.spinner} />
      <p>Loading report data...</p>
    </div>
  );

  // Shared error component
  const errorComponent = (error: Error) => (
    <div className={styles.errorContainer}>
      <Icons.ExclamationTriangle size={48} className={styles.errorIcon} />
      <h3>Failed to Load Report</h3>
      <p>{error.message}</p>
      <Button variant="primary" onClick={handleRefresh}>
        <Icons.ArrowClockwise size={16} /> Try Again
      </Button>
    </div>
  );

  // Get report name from config
  const reportName = "E-file Signature Authorization (8879) Report";

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.reportContainer}>
        {isMultiMode ? (
          <FlowReport
            key={`multi-${refreshKey}`}
            reportMode="multi"
            reportType={config.reportType}
            flowName={reportName}
            reportEndpoint="/workflow/flow/multi-report/"
            gridEndpoint="/workflow/flow/multi-grid/"
            onReportLoaded={handleReportLoaded}
            onError={handleReportError}
            initialPageSize={100}
            showHeader={true}
            showActionStats={true}
            xaxistitle=""
            beautifiedNames={config.beautifiedNames}
            multiFlowConfig={config.config}
            loadingComponent={loadingComponent}
            errorComponent={errorComponent}
            disclaimerText= "The summary and chart display only the latest record per client, providing a client-level overview."
          />
        ) : (
          // ═══════════════════════════════════════════════════════════════════
          // SINGLE MODE: Original single-flow report
          // ═══════════════════════════════════════════════════════════════════
          <FlowReport
            key={`single-${refreshKey}`}
            reportMode="single"
            reportType={config.reportType}
            flowId={config.flowId!}
            flowName={reportName}
            reportEndpoint="/workflow/flow/report/"
            gridEndpoint="/workflow/flow/grid/"
            onReportLoaded={handleReportLoaded}
            onError={handleReportError}
            initialPageSize={100}
            showHeader={true}
            showActionStats={true}
            xaxistitle=""
            beautifiedNames={config.beautifiedNames}
            baseColumnHeaders={config.baseColumnHeaders}
            loadingComponent={loadingComponent}
            errorComponent={errorComponent}
            disclaimerText= "The summary and chart display only the latest record per client, providing a client-level overview."
          />
        )}
      </div>

      <div className={styles.pageFooter}>
        <div className={styles.footerContent}>
          {/* Footer content if needed */}
        </div>
      </div>
    </div>
  );
};

export default FlowReport8879;